/**
 * ManuscriptViewer — shared interactive viewer for manuscript-backed demos.
 *
 * Orchestrates four collaborators:
 *   - `ManuscriptIndex`    — chapter list with filters, paths, and appendix expander
 *   - `ChapterReader`      — focused per-chapter view with safe Markdown + KaTeX
 *   - `buildMarkdownComponents` — dark-theme overrides for react-markdown
 *   - `loadChapter`        — injectable content source (caller supplies)
 *
 * Multiple manuscripts (monograph + handbook, etc.) are surfaced as a tab
 * strip at the top of the index — switching tabs reloads chapter content
 * from the active manuscript's loader on demand. The tab strip implements
 * the full WAI-ARIA tablist pattern: roving tabindex, ArrowLeft / ArrowRight
 * / Home / End keyboard navigation, paired `aria-controls` / `aria-labelledby`
 * between each tab and the displayed manuscript index (the tabpanel).
 *
 * Content loading is async-tolerant so callers can fetch chapters lazily.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { ChapterReader } from "./ChapterReader.js";
import { ManuscriptHeader } from "./ManuscriptHeader.js";
import { ManuscriptIndex } from "./ManuscriptIndex.js";
import { buildMarkdownComponents } from "./markdown-renderer.js";
import { resolveTheme } from "./theme.js";
import type {
  ManuscriptAppendix,
  ManuscriptChapter,
  ManuscriptItemKind,
  ManuscriptViewerProps,
} from "./types.js";

interface ViewingState {
  readonly manuscriptId: string;
  readonly item: ManuscriptChapter | ManuscriptAppendix;
  readonly kind: ManuscriptItemKind;
}

export function ManuscriptViewer({
  manuscripts,
  loadChapter,
  theme: themeOverride,
  rightSidebar,
  leadingContent,
  unavailableMessage = "Chapter content is not available in this build. Wire `loadChapter` to your content source.",
  hidePartBar = false,
  openItemRequest = null,
}: ManuscriptViewerProps): React.JSX.Element {
  const theme = useMemo(() => resolveTheme(themeOverride), [themeOverride]);
  const markdownComponents = useMemo(() => buildMarkdownComponents(theme), [theme]);
  const tabsId = useId();

  const firstManuscript = manuscripts[0];
  if (firstManuscript === undefined) {
    throw new Error("ManuscriptViewer requires at least one manuscript");
  }
  const manuscriptById = useMemo(
    () => new Map(manuscripts.map((manuscript) => [manuscript.id, manuscript])),
    [manuscripts],
  );
  const manuscriptItemByRequestKey = useMemo(() => {
    const items = new Map<string, ViewingState>();
    for (const manuscript of manuscripts) {
      for (const chapter of manuscript.chapters) {
        items.set(`${manuscript.id}:chapter:${chapter.id}`, {
          manuscriptId: manuscript.id,
          item: chapter,
          kind: "chapter",
        });
      }
      for (const appendix of manuscript.appendices ?? []) {
        items.set(`${manuscript.id}:appendix:${appendix.id}`, {
          manuscriptId: manuscript.id,
          item: appendix,
          kind: "appendix",
        });
      }
    }
    return items;
  }, [manuscripts]);
  const [activeManuscriptId, setActiveManuscriptId] = useState<string>(firstManuscript.id);
  const activeManuscript = manuscriptById.get(activeManuscriptId) ?? firstManuscript;

  const [viewing, setViewing] = useState<ViewingState | null>(null);
  const [content, setContent] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const lastOpenRequestKey = useRef<string | null>(null);

  useEffect(() => {
    if (openItemRequest === null) return;
    if (lastOpenRequestKey.current === openItemRequest.requestKey) return;
    lastOpenRequestKey.current = openItemRequest.requestKey;

    const target = manuscriptItemByRequestKey.get(
      `${openItemRequest.manuscriptId}:${openItemRequest.kind}:${openItemRequest.itemId}`,
    );
    if (target === undefined) return;

    setActiveManuscriptId(target.manuscriptId);
    setViewing(target);
  }, [manuscriptItemByRequestKey, openItemRequest]);

  useEffect(() => {
    if (viewing === null) {
      setContent(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const result = loadChapter({
      manuscriptId: viewing.manuscriptId,
      itemId: viewing.item.id,
      kind: viewing.kind,
    });
    Promise.resolve(result).then(
      (resolved) => {
        if (!cancelled) {
          setContent(resolved);
          setLoading(false);
        }
      },
      () => {
        if (!cancelled) {
          setContent(null);
          setLoading(false);
        }
      },
    );
    return (): void => {
      cancelled = true;
    };
  }, [loadChapter, viewing]);

  if (viewing !== null) {
    return (
      <ChapterReader
        manuscript={activeManuscript}
        item={viewing.item}
        kind={viewing.kind}
        content={content}
        loading={loading}
        onClose={() => {
          setViewing(null);
        }}
        theme={theme}
        unavailableMessage={unavailableMessage}
        markdownComponents={markdownComponents}
      />
    );
  }

  const showTabs = manuscripts.length > 1;
  const totalWords = activeManuscript.chapters.reduce((s, c) => s + (c.words ?? 0), 0);
  const tabIdFor = (id: string): string => `${tabsId}-tab-${id}`;
  const panelId = `${tabsId}-panel`;
  const activeTabId = tabIdFor(activeManuscript.id);

  return (
    <div
      style={{
        background: theme.void,
        color: theme.text,
        minHeight: "100vh",
        fontFamily: theme.serif,
      }}
    >
      <style>{`
        .csk-mv-row { transition: background-color 0.15s ease, transform 0.15s ease; }
        .csk-mv-row:hover { background-color: ${theme.surfaceMuted} !important; }
        .csk-mv-row:focus-visible { outline: 2px solid ${theme.accent}; outline-offset: -2px; }
        @media (max-width: 720px) {
          .csk-mv-grid { grid-template-columns: 1fr !important; }
          .csk-mv-grid > aside { border-left: none !important; border-top: 1px solid ${theme.surfaceMuted}; }
        }
      `}</style>
      <ManuscriptHeader
        manuscript={activeManuscript}
        manuscripts={manuscripts}
        showTabs={showTabs}
        totalWords={totalWords}
        theme={theme}
        tabIdFor={tabIdFor}
        panelId={panelId}
        onSwitchManuscript={(id) => {
          setActiveManuscriptId(id);
        }}
      />

      <div
        className="csk-mv-grid"
        style={{
          display: "grid",
          gridTemplateColumns: rightSidebar !== undefined ? "1fr 280px" : "1fr",
        }}
      >
        <div
          id={showTabs ? panelId : undefined}
          role={showTabs ? "tabpanel" : undefined}
          aria-labelledby={showTabs ? activeTabId : undefined}
          style={{
            borderRight: rightSidebar !== undefined ? `1px solid ${theme.surfaceMuted}` : undefined,
          }}
        >
          <ManuscriptIndex
            manuscript={activeManuscript}
            onOpen={(item, kind) => {
              setViewing({ manuscriptId: activeManuscript.id, item, kind });
            }}
            theme={theme}
            hidePartBar={hidePartBar}
            leadingContent={leadingContent}
          />
        </div>
        {rightSidebar !== undefined ? (
          <aside
            style={{
              padding: "20px 18px",
              background: theme.surface,
              fontSize: 11,
              color: theme.text,
            }}
          >
            {rightSidebar}
          </aside>
        ) : null}
      </div>
    </div>
  );
}
